import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Package, Receipt, Calendar, Printer, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { formatDate, formatDateTime } from '../utils/dateFormat';

import { useStore } from '../context/StoreContext';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesHistory = () => {
  const { storeName } = useStore();
  const { getAuthHeader } = useAuth();
  const [sales, setSales] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const [salesRes, posRes] = await Promise.all([
          axios.get(`${API_URL}/sales`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/purchase-orders`, { headers: getAuthHeader() })
        ]);
        setSales(salesRes.data);
        setPurchaseOrders(posRes.data);
      } catch (error) {
        console.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [getAuthHeader]);

  // Print sale receipt
  const printReceipt = (sale) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    const itemsHtml = (sale.items || []).map(item => `
      <tr>
        <td style="padding: 4px 0;">${item.quantity}x ${item.product_name}</td>
        <td style="text-align: right; padding: 4px 0;">$${(item.unit_price * item.quantity).toFixed(2)}</td>
      </tr>
    `).join('');
    
    const subtotal = sale.subtotal || (sale.total_amount / 1.15);
    const gst = sale.tax_amount || (sale.total_amount - subtotal);
    
    printWindow.document.write(`
      <html>
      <head>
        <title>Receipt - ${sale.id.slice(-8).toUpperCase()}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 20px; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .line { border-top: 1px dashed #000; margin: 10px 0; }
          table { width: 100%; border-collapse: collapse; }
          .total { font-size: 16px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="center bold" style="font-size: 16px;">${localStorage.getItem('business_name') || 'SupplyTrack'}</div>
        <div class="center">${(sale.store || 'orewa').toUpperCase()}</div>
        <div class="line"></div>
        <div><strong>Receipt:</strong> #${sale.id.slice(-8).toUpperCase()}</div>
        <div><strong>Date:</strong> ${formatDateTime(sale.created_at)}</div>
        ${sale.customer_name ? `<div><strong>Customer:</strong> ${sale.customer_name}</div>` : ''}
        <div class="line"></div>
        <table><tbody>${itemsHtml}</tbody></table>
        <div class="line"></div>
        <table>
          <tr><td>Subtotal (Ex GST):</td><td style="text-align: right;">$${subtotal.toFixed(2)}</td></tr>
          <tr><td>GST (15%):</td><td style="text-align: right;">$${gst.toFixed(2)}</td></tr>
        </table>
        <div class="line"></div>
        <table><tr class="total"><td>TOTAL:</td><td style="text-align: right;">$${sale.total_amount.toFixed(2)}</td></tr></table>
        <div class="line"></div>
        <div class="center">
          <div><strong>Payment:</strong> ${(sale.payment_method || 'CASH').toUpperCase()}</div>
          ${sale.split_payment ? `<div>Cash: $${sale.cash_amount?.toFixed(2)} | Card: $${sale.card_amount?.toFixed(2)}</div>` : ''}
        </div>
        <div class="line"></div>
        <div class="center" style="margin-top: 10px;">Thank you for your purchase!</div>
        <div class="center" style="font-size: 10px; margin-top: 5px;">Printed: ${new Date().toLocaleString()}</div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Get store badge
  const getStoreBadge = (store) => {
    if (store === 'eastridge') {
      return <Badge className="bg-blue-100 text-blue-700"><MapPin className="w-3 h-3 mr-1" />{storeName('eastridge')}</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700"><MapPin className="w-3 h-3 mr-1" />{storeName('orewa')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-history-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
          History
        </h1>
        <p className="text-slate-500 mt-1">View your sales and purchase order history</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sales" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="sales" data-testid="sales-history-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            <ShoppingCart className="w-4 h-4 mr-2" /> Sales ({sales.length})
          </TabsTrigger>
          <TabsTrigger value="purchases" data-testid="purchases-history-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            <Package className="w-4 h-4 mr-2" /> Purchases ({purchaseOrders.length})
          </TabsTrigger>
        </TabsList>

        {/* Sales History */}
        <TabsContent value="sales" className="mt-6">
          <div className="space-y-4">
            {sales.length === 0 ? (
              <Card className="border rounded-sm">
                <CardContent className="text-center py-12 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  No sales yet
                </CardContent>
              </Card>
            ) : (
              sales.map((sale) => (
                <Card key={sale.id} className="border rounded-sm card-hover" data-testid={`sale-record-${sale.id}`}>
                  <CardHeader className="pb-2 border-b bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                          <ShoppingCart className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base font-semibold text-slate-900">
                              Sale #{sale.id.slice(-8).toUpperCase()}
                            </CardTitle>
                            {getStoreBadge(sale.store)}
                          </div>
                          <div className="flex items-center text-sm text-slate-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(sale.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-emerald-600">
                            ${sale.total_amount.toFixed(2)}
                          </div>
                          <div className="text-xs text-slate-500 uppercase">Total</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printReceipt(sale)}
                          data-testid={`print-sale-${sale.id}`}
                        >
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500">Product</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-center">Qty</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-right">Unit Price</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-right">Subtotal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sale.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">${item.unit_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium">${(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Purchase Order History */}
        <TabsContent value="purchases" className="mt-6">
          <div className="space-y-4">
            {purchaseOrders.length === 0 ? (
              <Card className="border rounded-sm">
                <CardContent className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  No purchase orders yet
                </CardContent>
              </Card>
            ) : (
              purchaseOrders.map((po) => (
                <Card key={po.id} className="border rounded-sm card-hover" data-testid={`po-record-${po.id}`}>
                  <CardHeader className="pb-2 border-b bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                          <Package className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold text-slate-900">
                            PO #{po.id.slice(-8).toUpperCase()}
                          </CardTitle>
                          <div className="flex items-center text-sm text-slate-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(po.created_at)}
                            {po.supplier && <span className="ml-2">• {po.supplier}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">
                          ${po.total_amount.toFixed(2)}
                        </div>
                        <div className="text-xs text-slate-500 uppercase">Total</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500">Product</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-center">Qty</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-right">Buy Price</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-slate-500 text-right">Subtotal</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {po.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-center">{item.quantity}</TableCell>
                            <TableCell className="text-right">${item.buy_price.toFixed(2)}</TableCell>
                            <TableCell className="text-right font-medium">${(item.quantity * item.buy_price).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesHistory;
